import { addMonths, endOfMonth, isValid, startOfDay, startOfMonth } from "date-fns";
import type { SessionEvent } from "@/components/admin/locations/components/calendar/SessionEvent";
import type { Variation } from "@/components/admin/locations/types/variation";
import type { CampLocation } from "@/types/campLocation";
import type { Offer } from "@/types/offer";
import { ensureValidDate } from "@/utils/dateUtils";
import { extractLocationId } from "@/services/offer/offerCopyHelpers";
import { offerToSessionEvent } from "@/services/offer/offerDisplayUtils";

/** Sentinel filter key for sessions not in the top-N programs for the visible window. */
export const ORG_CALENDAR_OTHER_KEY = "__org_calendar_other__";

export const ORG_CALENDAR_TOP_PROGRAM_COUNT = 12;

export function getOrgCalendarTwoMonthWindow(baseMonth: Date): {
  windowStart: Date;
  windowEnd: Date;
} {
  const windowStart = startOfDay(startOfMonth(baseMonth));
  const windowEnd = startOfDay(endOfMonth(addMonths(baseMonth, 1)));
  return { windowStart, windowEnd };
}

export function offerOverlapsCalendarWindow(
  offer: Offer,
  windowStart: Date,
  windowEnd: Date
): boolean {
  const start = ensureValidDate(offer.startDate as Date | string | { toDate?: () => Date });
  const end = ensureValidDate(offer.endDate as Date | string | { toDate?: () => Date });
  if (!isValid(start) || !isValid(end)) return false;
  const ds = startOfDay(start);
  const de = startOfDay(end);
  return ds <= windowEnd && de >= windowStart;
}

export function computeTopVariationKeysForWindow(
  offers: Offer[],
  windowStart: Date,
  windowEnd: Date,
  topN: number
): string[] {
  const counts = new Map<string, number>();
  for (const o of offers) {
    const key = o.variationKey || "";
    if (!key) continue;
    if (!offerOverlapsCalendarWindow(o, windowStart, windowEnd)) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const entries = [...counts.entries()].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return a[0].localeCompare(b[0]);
  });
  return entries.slice(0, topN).map(([k]) => k);
}

function displayTitleForVariationKey(
  key: string,
  locations: CampLocation[]
): string {
  for (const loc of locations) {
    const v = loc.variations?.find((x) => x.key === key);
    if (v?.displayTitle) return v.displayTitle;
  }
  return key;
}

function sessionTitleWithLocation(
  offer: Offer,
  locations: CampLocation[]
): string {
  const base = offer.displayTitle || "";
  const locId = extractLocationId(offer);
  if (!locId) return base;
  const name = locations.find((l) => l.id === locId)?.name;
  if (!name) return base;
  return `${base} (${name})`;
}

export interface BuildOrgCalendarSessionEventsResult {
  sessionEvents: SessionEvent[];
  topVariationKeys: string[];
  legendVariations: Variation[];
}

/**
 * Build calendar session events for an organization: top programs (by session count in the
 * two visible months) get distinct colors; remaining programs in that window use gray.
 */
export function buildOrgCalendarSessionEvents(
  offers: Offer[],
  locations: CampLocation[],
  baseMonth: Date,
  topN: number = ORG_CALENDAR_TOP_PROGRAM_COUNT
): BuildOrgCalendarSessionEventsResult {
  const { windowStart, windowEnd } = getOrgCalendarTwoMonthWindow(baseMonth);
  const topKeys = computeTopVariationKeysForWindow(
    offers,
    windowStart,
    windowEnd,
    topN
  );
  const topSet = new Set(topKeys);

  let needsOtherLegend = false;
  for (const o of offers) {
    if (!offerOverlapsCalendarWindow(o, windowStart, windowEnd)) continue;
    const k = o.variationKey || "";
    if (!k || !topSet.has(k)) {
      needsOtherLegend = true;
      break;
    }
  }

  const sessionEvents: SessionEvent[] = offers.map((offer) => {
    const key = offer.variationKey || "";
    const overlaps = offerOverlapsCalendarWindow(offer, windowStart, windowEnd);

    let programIndex: number | undefined;
    if (overlaps && key && topSet.has(key)) {
      programIndex = topKeys.indexOf(key);
    }

    const event = offerToSessionEvent(offer, programIndex);
    const title = sessionTitleWithLocation(offer, locations);

    if (overlaps && (!key || !topSet.has(key))) {
      return { ...event, title, colorScheme: "gray" as const };
    }

    return { ...event, title };
  });

  const legendVariations: Variation[] = topKeys.map((k) => ({
    key: k,
    displayTitle: displayTitleForVariationKey(k, locations),
  }));

  if (needsOtherLegend) {
    legendVariations.push({
      key: ORG_CALENDAR_OTHER_KEY,
      displayTitle: "Other",
      colorScheme: "gray",
    });
  }

  return { sessionEvents, topVariationKeys: topKeys, legendVariations };
}
